import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { X, ExternalLink, FileText, BookOpen, ChevronRight, Download } from 'lucide-react';

const DocumentViewer = ({ docId, onClose }) => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState(new Set());

  useEffect(() => {
    if (docId) {
      fetchDocument();
    }
  }, [docId]);

  const fetchDocument = async () => {
    const data = await apiFetch(`${API}/documents/${docId}/content`);
    if (data?._error) {
      setError('Failed to load document');
      setLoading(false);
      return;
    }
    setDocument(data);
    setLoading(false);

    // Expand first section by default
    if (data.content?.sections?.length > 0) {
      setExpandedSections(new Set([data.content.sections[0].section_number]));
    }
  };

  const toggleSection = (sectionNumber) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionNumber)) {
      newExpanded.delete(sectionNumber);
    } else {
      newExpanded.add(sectionNumber);
    }
    setExpandedSections(newExpanded);
  };

  if (!docId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-6 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen size={32} />
            <div>
              <h2 className="text-2xl font-bold">
                {loading ? 'Loading...' : document?.metadata?.name}
              </h2>
              {document?.metadata && (
                <p className="text-indigo-100 text-sm">
                  {document.metadata.code} • {document.metadata.jurisdiction} • {document.metadata.year}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {document && !loading && (
            <div className="space-y-6">
              {/* Document Summary */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-900 mb-2">Summary</h3>
                <p className="text-sm text-indigo-800">{document.metadata.summary}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {document.metadata.key_topics.map((topic, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              {/* Content Sections or Link */}
              {document.has_embedded_content && document.content ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <FileText size={20} className="text-indigo-600" />
                    Key Sections & Articles
                  </h3>
                  
                  {document.content.sections.map((section, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection(section.section_number)}
                        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {section.section_number}
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{section.title}</h4>
                            <p className="text-xs text-slate-600">Section {section.section_number}</p>
                          </div>
                        </div>
                        <ChevronRight
                          size={20}
                          className={`text-slate-400 transition-transform ${
                            expandedSections.has(section.section_number) ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                      
                      {expandedSections.has(section.section_number) && (
                        <div className="p-4 bg-white border-t border-slate-200">
                          <div className="prose prose-sm max-w-none">
                            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                              {section.content}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900 mb-2">
                      📖 <span className="font-semibold">Note:</span> These are key excerpts from the official regulation. 
                      For complete details, legal interpretation, and the latest amendments, always refer to the official source.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <FileText size={48} className="text-yellow-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-yellow-900 mb-2">Full Document Not Yet Embedded</h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    This document's full text will be added in a future update. 
                    In the meantime, you can access the official version directly.
                  </p>
                </div>
              )}

              {/* Official Source Link */}
              <div className="flex items-center justify-between p-4 bg-slate-100 rounded-lg">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Official Source</p>
                  <p className="text-xs text-slate-600">View on EUR-Lex or official website</p>
                </div>
                <a
                  href={document.metadata.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  <ExternalLink size={16} />
                  View Official PDF
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
